import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, Paper, Grid, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AccountBalance, TrendingUp, AttachMoney, Assessment } from '@mui/icons-material';
import CompactMetricCard from '../components/common/CompactMetricCard';
import apiClient from '../api/client';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface FinancePageProps {
  startDate: string | null;
  endDate: string | null;
}

interface FinanceMetrics {
  revenue: {
    period_value: number;
    last_month_value: number;
    currency: string;
  };
  gross_margin: {
    period_percentage: number;
    last_month_percentage: number;
  };
  ebitda: {
    period_percentage: number;
    last_month_percentage: number;
  };
}

interface MonthlyData {
  month: string;
  month_key: string;
  revenue: number;
  gross_profit: number;
  ebitda: number;
  cost_of_sales: number;
  sga_expenses: number;
  corporate_staff_cost: number;
  travel_expenses: number;
}

interface FinanceData {
  period: {
    start_date: string;
    end_date: string;
  };
  metrics: FinanceMetrics;
  breakdown: {
    total_revenue: number;
    last_month_revenue: number;
    gross_margin_percentage: number;
    last_month_gross_margin_percentage: number;
    ebitda_percentage: number;
    last_month_ebitda_percentage: number;
  };
  monthly_data: MonthlyData[];
}

interface SgaTravelData {
  period: {
    start_date: string;
    end_date: string;
  };
  monthly_data: {
    month: string;
    month_key: string;
    revenue: number;
    sga_cost: number;
    travel_cost: number;
    sga_travel_total: number;
    sga_travel_percentage: number;
  }[];
  summary: {
    avg_sga_travel_percentage: number;
    total_sga_travel_cost: number;
    total_revenue: number;
  };
}

interface CustomerTurnoverData {
  period: {
    start_date: string;
    end_date: string;
  };
  period_total_revenue: number;
  period_summary: {
    customer_name: string;
    total_revenue: number;
    percentage: number;
  }[];
  monthly_breakdown: {
    month: string;
    total_revenue: number;
    customers: {
      customer_name: string;
      revenue: number;
      percentage: number;
    }[];
  }[];
}

const FinancePage: React.FC<FinancePageProps> = ({ startDate, endDate }) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  // Keep charts readable on mobile, slightly smaller on md/lg/xl
  const chartFont = useMemo(
    () => ({
      legend: isMdUp ? 10 : 11,
      ticks: isMdUp ? 9 : 10,
      tooltipTitle: isMdUp ? 12 : 13,
      tooltipBody: isMdUp ? 11 : 12,
      axisTitle: isMdUp ? 10 : 12,
      legendPadding: isMdUp ? 14 : 18,
    }),
    [isMdUp],
  );

  const [financeData, setFinanceData] = useState<FinanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SG&A + Travel Analysis
  const [sgaTravelData, setSgaTravelData] = useState<SgaTravelData | null>(null);
  const [sgaTravelLoading, setSgaTravelLoading] = useState(false);
  const [sgaTravelError, setSgaTravelError] = useState<string | null>(null);

  // Customer Turnover Analysis
  const [customerTurnoverData, setCustomerTurnoverData] = useState<CustomerTurnoverData | null>(null);
  const [customerTurnoverLoading, setCustomerTurnoverLoading] = useState(false);
  const [customerTurnoverError, setCustomerTurnoverError] = useState<string | null>(null);

  // EBITDA Analysis period selection
  const [startPeriod, setStartPeriod] = useState<string>('');
  const [endPeriod, setEndPeriod] = useState<string>('');
  const [ebitdaAnalysisData, setEbitdaAnalysisData] = useState<any>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  // Point selection for Gross Margin & EBITDA % chart
  const [selectedPoints, setSelectedPoints] = useState<Array<{ datasetIndex: number; index: number; month: string; value: number; label: string }>>([]);
  const chartRef = useRef<ChartJS<'line'>>(null);

  // Handle point click for Gross Margin & EBITDA % chart
  const handleChartClick = (event: any, elements: any[], chart: any) => {
    if (!financeData || !elements || elements.length === 0) return;

    const element = elements[0];
    const datasetIndex = element.datasetIndex;
    const index = element.index;

    if (datasetIndex === undefined || index === undefined) return;

    const dataset = chart.data.datasets[datasetIndex];
    const value = dataset.data[index] as number;
    const month = chart.data.labels?.[index] as string;
    const label = dataset.label || '';

    const newPoint = { datasetIndex, index, month, value, label };

    setSelectedPoints(prev => {
      // If clicking the same point, deselect it
      const existingIndex = prev.findIndex(
        p => p.datasetIndex === datasetIndex && p.index === index
      );
      if (existingIndex !== -1) {
        return prev.filter((_, i) => i !== existingIndex);
      }

      // If 2 points already selected, replace the first one
      if (prev.length >= 2) {
        return [prev[1], newPoint];
      }

      // Add new point
      return [...prev, newPoint];
    });
  };

  // Reset selected points when data changes
  useEffect(() => {
    setSelectedPoints([]);
  }, [financeData]);

  // Force chart update when selectedPoints changes
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update('none'); // 'none' prevents animation
    }
  }, [selectedPoints]);

  // Create memoized plugin that reacts to selectedPoints changes
  const pointSelectionPlugin = useMemo(() => ({
    id: 'pointSelectionPlugin',
    afterDraw: (chart: any) => {
      const points = selectedPoints;
      if (points.length === 2) {
        const ctx = chart.ctx;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        const chartArea = chart.chartArea;

        const point1 = points[0];
        const point2 = points[1];

        // Get pixel positions for both points
        const x1 = xScale.getPixelForValue(point1.index);
        const x2 = xScale.getPixelForValue(point2.index);

        const dataset1 = chart.data.datasets[point1.datasetIndex];
        const dataset2 = chart.data.datasets[point2.datasetIndex];

        const y1 = yScale.getPixelForValue(dataset1.data[point1.index] as number);
        const y2 = yScale.getPixelForValue(dataset2.data[point2.index] as number);

        // Draw quadrilateral connecting the two points
        // Create a shape from point1 to point2, extending to bottom of chart area
        ctx.save();
        ctx.fillStyle = 'rgba(239, 68, 68, 0.15)';
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.moveTo(x1, y1); // Start at first point
        ctx.lineTo(x2, y2); // Line to second point
        ctx.lineTo(x2, chartArea.bottom); // Down to bottom
        ctx.lineTo(x1, chartArea.bottom); // Across to first point's x position
        ctx.closePath(); // Back to start
        ctx.fill();
        ctx.stroke();
        ctx.restore();

        // Draw connecting line between the two points
        ctx.save();
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        ctx.restore();

        // Highlight selected points with red circles
        points.forEach(point => {
          const x = xScale.getPixelForValue(point.index);
          const dataset = chart.data.datasets[point.datasetIndex];
          const y = yScale.getPixelForValue(dataset.data[point.index] as number);

          ctx.save();
          ctx.fillStyle = '#ef4444';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(x, y, 8, 0, 2 * Math.PI);
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        });
      } else if (points.length === 1) {
        // Highlight single selected point
        const ctx = chart.ctx;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;

        const point = points[0];
        const x = xScale.getPixelForValue(point.index);
        const dataset = chart.data.datasets[point.datasetIndex];
        const y = yScale.getPixelForValue(dataset.data[point.index] as number);

        ctx.save();
        ctx.fillStyle = '#ef4444';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    }
  }), [selectedPoints]);

  // Generate available months for period selection
  const getAvailableMonths = () => {
    if (!financeData || !financeData.monthly_data) return [];
    return financeData.monthly_data.map(d => ({
      value: d.month_key,
      label: d.month
    }));
  };

  const fetchFinanceData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/api/v1/delivery/finance-dashboard', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });

      setFinanceData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch finance data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch SG&A + Travel analysis data
  const fetchSgaTravelAnalysis = useCallback(async () => {
    if (!startDate || !endDate) return;

    setSgaTravelLoading(true);
    setSgaTravelError(null);

    try {
      const response = await apiClient.get('/api/v1/delivery/sga-travel-analysis', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      setSgaTravelData(response.data);
    } catch (err: any) {
      setSgaTravelError(err.response?.data?.detail || 'Error fetching SG&A + Travel analysis');
    } finally {
      setSgaTravelLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch Customer Turnover analysis data
  const fetchCustomerTurnoverAnalysis = useCallback(async () => {
    if (!startDate || !endDate) return;

    setCustomerTurnoverLoading(true);
    setCustomerTurnoverError(null);

    try {
      const response = await apiClient.get('/api/v1/delivery/customer-turnover-analysis', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      setCustomerTurnoverData(response.data);
    } catch (err: any) {
      setCustomerTurnoverError(err.response?.data?.detail || 'Error fetching customer turnover analysis');
    } finally {
      setCustomerTurnoverLoading(false);
    }
  }, [startDate, endDate]);

  // Fetch EBITDA analysis data
  const fetchEbitdaAnalysis = useCallback(async () => {
    if (!startPeriod || !endPeriod) return;

    setAnalysisLoading(true);
    setAnalysisError(null);

    try {
      const response = await apiClient.get('/api/v1/delivery/ebitda-period-analysis', {
        params: {
          start_period: startPeriod,
          end_period: endPeriod
        }
      });
      setEbitdaAnalysisData(response.data);
    } catch (err: any) {
      setAnalysisError(err.response?.data?.detail || 'Error fetching EBITDA analysis');
    } finally {
      setAnalysisLoading(false);
    }
  }, [startPeriod, endPeriod]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchFinanceData();
      fetchSgaTravelAnalysis();
      fetchCustomerTurnoverAnalysis();
    }
  }, [startDate, endDate, fetchFinanceData, fetchSgaTravelAnalysis, fetchCustomerTurnoverAnalysis]);

  // Fetch EBITDA analysis when both periods are selected
  useEffect(() => {
    if (startPeriod && endPeriod) {
      fetchEbitdaAnalysis();
    }
  }, [startPeriod, endPeriod, fetchEbitdaAnalysis]);

  const formatCurrency = (value: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto', position: 'relative' }}>
      {/* Page Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <AccountBalance sx={{ fontSize: 32, color: '#1e40af' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Finance Dashboard
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Financial overview and analysis for the selected period
        </Typography>
      </Box> */}

      <>
          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Finance Cards */}
          {financeData && !loading && (
            <Grid container spacing={1.5} alignItems="stretch" sx={{ mb: 2 }}>
              {/* Revenue Card */}
              <Grid item xs={12} sm={6} md={4}>
                <CompactMetricCard
                  title="Revenue"
                  value={formatCurrency(financeData.metrics.revenue.period_value, financeData.metrics.revenue.currency)}
                  subtitle={`Last Month: ${formatCurrency(financeData.metrics.revenue.last_month_value, financeData.metrics.revenue.currency)}`}
                  icon={TrendingUp}
                  color="primary"
                />
              </Grid>

              {/* Gross Margin Card */}
              <Grid item xs={12} sm={6} md={4}>
                <CompactMetricCard
                  title="GM%"
                  value={`${financeData.metrics.gross_margin.period_percentage}%`}
                  subtitle={`Last Month: ${financeData.metrics.gross_margin.last_month_percentage}%`}
                  icon={AttachMoney}
                  color="success"
                />
              </Grid>

              {/* EBITDA Card */}
              <Grid item xs={12} sm={6} md={4}>
                <CompactMetricCard
                  title="EBITDA%"
                  value={`${financeData.metrics.ebitda.period_percentage}%`}
                  subtitle={`Last Month: ${financeData.metrics.ebitda.last_month_percentage}%`}
                  icon={Assessment}
                  color="warning"
                />
              </Grid>
            </Grid>
          )}

          {/* Gross Margin & EBITDA % Chart */}
          {financeData && !loading && financeData.monthly_data && financeData.monthly_data.length > 0 && (
            <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 4, height: { xs: 420, sm: 480, md: 500 }, position: 'relative' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: '#0f172a',
                  fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                }}
              >
                Gross Margin & EBITDA as % of Revenue
              </Typography>
              {selectedPoints.length === 2 && (
                <Box sx={{
                  position: 'absolute',
                  top: { xs: 56, sm: 60 },
                  right: { xs: 12, sm: 20 },
                  left: { xs: 12, sm: 'auto' },
                  backgroundColor: 'rgba(0, 0, 0, 0.85)',
                  color: '#ffffff',
                  padding: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  zIndex: 10,
                  minWidth: { xs: 'unset', sm: 250 },
                  maxWidth: { xs: 'calc(100% - 24px)', sm: 320 },
                  border: '1px solid #e5e7eb'
                }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' } }}
                  >
                    Comparison
                  </Typography>
                  {selectedPoints.map((point, idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' }, lineHeight: 1.35 }}>
                        <strong>{point.label}</strong> - {point.month}: {point.value.toFixed(2)}%
                      </Typography>
                    </Box>
                  ))}
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' }, lineHeight: 1.35 }}>
                      <strong>Difference:</strong> {(selectedPoints[1].value - selectedPoints[0].value).toFixed(2)}%
                    </Typography>
                  </Box>
                </Box>
              )}
              <Box sx={{ height: { xs: 320, sm: 380, md: 400 }, position: 'relative' }}>
                <Line
                  ref={chartRef}
                  data={{
                    labels: financeData.monthly_data.map(d => d.month),
                    datasets: [
                      {
                        label: 'Gross Margin %',
                        data: financeData.monthly_data.map(d =>
                          d.revenue > 0 ? ((d.gross_profit / d.revenue) * 100) : 0
                        ),
                        borderColor: '#10B981',
                        backgroundColor: 'rgba(30, 64, 175, 0.1)',
                        borderWidth: 3,
                        fill: false,
                        tension: 0.4,
                        pointBackgroundColor: '#10B981',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 2,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                        yAxisID: 'y',
                      },
                      {
                        label: 'EBITDA %',
                        data: financeData.monthly_data.map(d =>
                          d.revenue > 0 ? ((d.ebitda / d.revenue) * 100) : 0
                        ),
                        borderColor: '#d97706',
                        backgroundColor: 'rgba(217, 119, 6, 0.1)',
                        borderWidth: 3,
                        fill: false,
                        tension: 0.4,
                        pointBackgroundColor: '#d97706',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 2,
                        pointRadius: 6,
                        pointHoverRadius: 8,
                        yAxisID: 'y',
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                    onClick: (event, elements, chart) => {
                      handleChartClick(event, elements, chart);
                    },
                    onHover: (event, activeElements) => {
                      if (event.native && event.native.target) {
                        (event.native.target as HTMLElement).style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
                      }
                    },
                    plugins: {
                      legend: {
                        position: 'top' as const,
                        labels: {
                          usePointStyle: true,
                          padding: chartFont.legendPadding,
                          color: '#374151',
                          font: {
                            size: chartFont.legend,
                            weight: 'normal'
                          }
                        }
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: '#ffffff',
                        bodyColor: '#ffffff',
                        borderColor: '#e5e7eb',
                        borderWidth: 1,
                        titleFont: {
                          size: chartFont.tooltipTitle,
                          weight: 'bold'
                        },
                        bodyFont: {
                          size: chartFont.tooltipBody
                        },
                        callbacks: {
                          label: function (context) {
                            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false
                        },
                        ticks: {
                          color: '#64748b',
                          font: {
                            size: chartFont.ticks,
                            weight: 'normal'
                          }
                        }
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)'
                        },
                        ticks: {
                          color: '#64748b',
                          font: {
                            size: chartFont.ticks,
                            weight: 'normal'
                          },
                          callback: function (value) {
                            return typeof value === 'number' ? value.toFixed(1) + '%' : Number(value).toFixed(1) + '%';
                          }
                        }
                      }
                    }
                  }}
                  plugins={[pointSelectionPlugin]}
                />
              </Box>
            </Paper>
          )}

          {/* Financial Charts - Side by Side */}
          {financeData && !loading && financeData.monthly_data && financeData.monthly_data.length > 0 && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Financial Overview Chart */}
              <Grid item xs={12} md={12}>
                <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, height: { xs: 420, sm: 480, md: 500 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1.5, sm: 2 },
                      color: '#0f172a',
                      fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                    }}
                  >
                    Financial Overview - Revenue, Gross Margin & EBITDA
                  </Typography>
                  <Box sx={{ height: { xs: 320, sm: 380, md: 400 } }}>
                    <Line
                      data={{
                        labels: financeData.monthly_data.map(d => d.month),
                        datasets: [
                          {
                            label: 'Revenue (₹)',
                            data: financeData.monthly_data.map(d => d.revenue),
                            borderColor: '#3B82F6',
                            backgroundColor: 'rgba(5, 150, 105, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#3B82F6',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                            yAxisID: 'y',
                          },
                          {
                            label: 'Gross Profit (₹)',
                            data: financeData.monthly_data.map(d => d.gross_profit),
                            borderColor: '#10B981',
                            backgroundColor: 'rgba(30, 64, 175, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#10B981',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                            yAxisID: 'y',
                          },
                          {
                            label: 'EBITDA (₹)',
                            data: financeData.monthly_data.map(d => d.ebitda),
                            borderColor: '#d97706',
                            backgroundColor: 'rgba(217, 119, 6, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#d97706',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                            yAxisID: 'y',
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'index' as const,
                          intersect: false,
                        },
                        plugins: {
                          legend: {
                            position: 'top' as const,
                            labels: {
                              usePointStyle: true,
                              padding: chartFont.legendPadding,
                              color: '#374151',
                              font: {
                                size: chartFont.legend,
                                weight: 'normal'
                              }
                            }
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            titleColor: '#ffffff',
                            bodyColor: '#ffffff',
                            borderColor: '#e5e7eb',
                            borderWidth: 1,
                            titleFont: {
                              size: chartFont.tooltipTitle,
                              weight: 'bold'
                            },
                            bodyFont: {
                              size: chartFont.tooltipBody
                            },
                            callbacks: {
                              label: function (context) {
                                return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              color: '#64748b',
                              font: {
                                size: chartFont.ticks,
                                weight: 'normal'
                              }
                            }
                          },
                          y: {
                            display: true,
                            position: 'left' as const,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.1)'
                            },
                            ticks: {
                              color: '#64748b',
                              font: {
                                size: chartFont.ticks,
                                weight: 'normal'
                              },
                              callback: function (value: any) {
                                return '₹' + (Number(value) / 1000000).toFixed(1) + 'M';
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Expenses Chart */}
              {/* <Grid item xs={12} md={6}>
                <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, height: { xs: 420, sm: 480, md: 500 } }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1.5, sm: 2 },
                      color: '#0f172a',
                      fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                    }}
                  >
                    Expenses Overview - SG&A, Corporate Staff & Travel
                  </Typography>
                  <Box sx={{ height: { xs: 320, sm: 380, md: 400 } }}>
                    <Line
                      data={{
                        labels: financeData.monthly_data.map(d => d.month),
                        datasets: [
                          {
                            label: 'SG&A Expenses (₹)',
                            data: financeData.monthly_data.map(d => d.sga_expenses),
                            borderColor: '#dc2626',
                            backgroundColor: 'rgba(220, 38, 38, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#dc2626',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                          },
                          {
                            label: 'Corporate Staff Cost (₹)',
                            data: financeData.monthly_data.map(d => d.corporate_staff_cost),
                            borderColor: '#7c3aed',
                            backgroundColor: 'rgba(124, 58, 237, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#7c3aed',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                          },
                          {
                            label: 'Travel Expenses (₹)',
                            data: financeData.monthly_data.map(d => d.travel_expenses),
                            borderColor: '#ea580c',
                            backgroundColor: 'rgba(234, 88, 12, 0.1)',
                            borderWidth: 3,
                            fill: false,
                            tension: 0.4,
                            pointBackgroundColor: '#ea580c',
                            pointBorderColor: '#ffffff',
                            pointBorderWidth: 2,
                            pointRadius: 6,
                            pointHoverRadius: 8,
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'index' as const,
                          intersect: false,
                        },
                        plugins: {
                          legend: {
                            position: 'top' as const,
                            labels: {
                              usePointStyle: true,
                              padding: chartFont.legendPadding,
                              color: '#374151',
                              font: {
                                size: chartFont.legend,
                                weight: 'normal'
                              }
                            }
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            titleColor: '#ffffff',
                            bodyColor: '#ffffff',
                            borderColor: '#e5e7eb',
                            borderWidth: 1,
                            titleFont: {
                              size: chartFont.tooltipTitle,
                              weight: 'bold'
                            },
                            bodyFont: {
                              size: chartFont.tooltipBody
                            },
                            callbacks: {
                              label: function (context) {
                                return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false
                            },
                            ticks: {
                              color: '#64748b',
                              font: {
                                size: chartFont.ticks,
                                weight: 'normal'
                              }
                            }
                          },
                          y: {
                            display: true,
                            position: 'left' as const,
                            grid: {
                              color: 'rgba(0, 0, 0, 0.1)'
                            },
                            ticks: {
                              color: '#64748b',
                              font: {
                                size: chartFont.ticks,
                                weight: 'normal'
                              },
                              callback: function (value: any) {
                                return '₹' + (Number(value) / 100000).toFixed(1) + 'L';
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Paper>
              </Grid> */}
            </Grid>
          )}

          {/* SG&A + Travel Cost Analysis Section */}
          {financeData && !loading && financeData.monthly_data && financeData.monthly_data.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: '#0f172a',
                  fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                }}
              >
                SG&A + Travel Cost Efficiency Analysis
              </Typography>

              <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 1.5, sm: 2 },
                    color: '#0f172a',
                    fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                  }}
                >
                  Sales Cost Efficiency vs Revenue
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    mb: { xs: 2, sm: 3 },
                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                  }}
                >
                  Analysis of SG&A + Travel costs as percentage of revenue to understand sales cost efficiency
                </Typography>

                {sgaTravelLoading ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 2 }}>
                      Loading SG&A + Travel analysis...
                    </Typography>
                  </Box>
                ) : sgaTravelError ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {sgaTravelError}
                  </Alert>
                ) : sgaTravelData && sgaTravelData.monthly_data && sgaTravelData.monthly_data.length > 0 ? (
                  <Box>
                    {/* Summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, backgroundColor: '#ffffff', textAlign: 'center' }}>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: '#dc2626',
                              mb: 1,
                              fontSize: 'clamp(1.2rem, 1.6vw, 1.6rem)',
                              lineHeight: 1.1,
                            }}
                          >
                            {sgaTravelData.summary.avg_sga_travel_percentage}%
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#6b7280', fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' } }}
                          >
                            Avg SG&A + Travel % of Revenue
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, backgroundColor: '#ffffff', textAlign: 'center' }}>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: '#F59E0B',
                              mb: 1,
                              fontSize: 'clamp(1.2rem, 1.6vw, 1.6rem)',
                              lineHeight: 1.1,
                            }}
                          >
                            {formatCurrency(sgaTravelData.summary.total_sga_travel_cost)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#6b7280', fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' } }}
                          >
                            Total SG&A + Travel Cost
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, backgroundColor: '#ffffff', textAlign: 'center' }}>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: '#1E40AF',
                              mb: 1,
                              fontSize: 'clamp(1.2rem, 1.6vw, 1.6rem)',
                              lineHeight: 1.1,
                            }}
                          >
                            {formatCurrency(sgaTravelData.summary.total_revenue)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: '#6b7280', fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' } }}
                          >
                            Total Revenue
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* SG&A + Travel Percentage Chart */}
                    {/* <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#ffffff' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: { xs: 1.5, sm: 2 },
                          color: '#374151',
                          fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                        }}
                      >
                        SG&A + Travel Costs as % of Revenue
                      </Typography>
                      <Box sx={{ height: { xs: 320, sm: 380, md: 400 } }}>
                        <Line
                          data={{
                            labels: sgaTravelData.monthly_data.map((d: SgaTravelData['monthly_data'][0]) => d.month),
                            datasets: [
                              {
                                label: 'SG&A + Travel % of Revenue',
                                data: sgaTravelData.monthly_data.map((d: SgaTravelData['monthly_data'][0]) => d.sga_travel_percentage),
                                borderColor: '#dc2626',
                                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                                borderWidth: 3,
                                fill: true,
                                tension: 0.4,
                                pointBackgroundColor: '#dc2626',
                                pointBorderColor: '#ffffff',
                                pointBorderWidth: 2,
                                pointRadius: 6,
                                pointHoverRadius: 8,
                              }
                            ]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            interaction: {
                              mode: 'index' as const,
                              intersect: false,
                            },
                            plugins: {
                              legend: {
                                position: 'top' as const,
                                labels: {
                                  usePointStyle: true,
                                  padding: chartFont.legendPadding,
                                  color: '#374151',
                                  font: {
                                    size: chartFont.legend,
                                    weight: 'normal'
                                  }
                                }
                              },
                              tooltip: {
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                titleColor: '#ffffff',
                                bodyColor: '#ffffff',
                                borderColor: '#e5e7eb',
                                borderWidth: 1,
                                titleFont: {
                                  size: chartFont.tooltipTitle,
                                  weight: 'bold'
                                },
                                bodyFont: {
                                  size: chartFont.tooltipBody
                                },
                                callbacks: {
                                  label: function (context: any) {
                                    const dataPoint = sgaTravelData.monthly_data[context.dataIndex];
                                    return [
                                      `SG&A + Travel %: ${context.parsed.y.toFixed(2)}%`,
                                      `SG&A Cost: ${formatCurrency(dataPoint.sga_cost)}`,
                                      `Travel Cost: ${formatCurrency(dataPoint.travel_cost)}`,
                                      `Total Cost: ${formatCurrency(dataPoint.sga_travel_total)}`,
                                      `Revenue: ${formatCurrency(dataPoint.revenue)}`
                                    ];
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                grid: {
                                  display: false
                                },
                                ticks: {
                                  color: '#64748b',
                                  font: {
                                    size: chartFont.ticks,
                                    weight: 'normal'
                                  }
                                }
                              },
                              y: {
                                display: true,
                                position: 'left' as const,
                                grid: {
                                  color: 'rgba(0, 0, 0, 0.1)'
                                },
                                ticks: {
                                  color: '#64748b',
                                  font: {
                                    size: chartFont.ticks,
                                    weight: 'normal'
                                  },
                                  callback: function (value: any) {
                                    return value.toFixed(1) + '%';
                                  }
                                },
                                title: {
                                  display: true,
                                  text: 'Percentage of Revenue (%)',
                                  color: '#374151',
                                  font: {
                                    size: chartFont.axisTitle,
                                    weight: 'bold'
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    </Paper> */}
                  </Box>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 2, border: '2px dashed #d1d5db' }}>
                    <Typography variant="h6" sx={{ color: '#6b7280', mb: 1 }}>
                      No SG&A + Travel Data Available
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                      No data found for the selected period
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* Customer Turnover Analysis Section */}
          {startDate && endDate && (
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: '#0f172a',
                  fontSize: 'clamp(0.95rem, 1.05vw, 1.05rem)',
                }}
              >
                Customer Turnover Analysis
              </Typography>

              <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                {customerTurnoverLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : customerTurnoverError ? (
                  <Alert severity="error">
                    {customerTurnoverError}
                  </Alert>
                ) : customerTurnoverData ? (
                  <Box>
                    {/* Period Summary Table */}
                    <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: { xs: 2, sm: 3 }, backgroundColor: '#ffffff' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: { xs: 1.25, sm: 1.75 },
                          color: '#374151',
                          fontSize: 'clamp(0.9rem, 0.9vw, 1rem)',
                        }}
                      >
                        Customer Revenue Contribution (Period Total)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6b7280',
                          mb: { xs: 2, sm: 3 },
                          fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                        }}
                      >
                        Total Period Revenue: ${customerTurnoverData.period_total_revenue.toLocaleString()}
                      </Typography>

                      <TableContainer>
                        <Table
                          sx={{
                            '& .MuiTableCell-root': {
                              fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.8rem' },
                              py: { xs: 0.75, sm: 1 },
                              px: { xs: 1.25, sm: 1.5 },
                            },
                            '& .MuiTableCell-head': {
                              fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.78rem' },
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Customer</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>Revenue (USD)</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>% of Total</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700, color: '#374151' }}>Revenue Segment</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerTurnoverData.period_summary
                              .filter(customer => customer.total_revenue > 0)
                              .map((customer, index) => {
                                // Calculate cumulative percentage to determine 80/20 split
                                const cumulativePercentage = customerTurnoverData.period_summary
                                  .filter(c => c.total_revenue > 0)
                                  .slice(0, index + 1)
                                  .reduce((sum, c) => sum + c.percentage, 0);

                                const isTop80 = cumulativePercentage <= 80;

                                return (
                                  <TableRow key={customer.customer_name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell component="th" scope="row">
                                      {customer.customer_name}
                                    </TableCell>
                                    <TableCell align="right">
                                      ${customer.total_revenue.toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Chip
                                        label={`${customer.percentage}%`}
                                        color={customer.percentage > 20 ? 'primary' : customer.percentage > 10 ? 'secondary' : 'default'}
                                        size="small"
                                        sx={{
                                          fontSize: { xs: '0.72rem', sm: '0.75rem', md: '0.72rem' },
                                          height: { xs: 18, sm: 20 },
                                          '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        label={isTop80 ? "Top 80%" : "Bottom 20%"}
                                        color={isTop80 ? 'success' : 'warning'}
                                        size="small"
                                        variant={isTop80 ? 'filled' : 'outlined'}
                                        sx={{
                                          fontSize: { xs: '0.72rem', sm: '0.75rem', md: '0.72rem' },
                                          height: { xs: 18, sm: 20 },
                                          '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                                        }}
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    {/* Monthly Breakdown - Pivot Table */}
                    <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#ffffff' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: { xs: 1.25, sm: 1.75 },
                          color: '#374151',
                          fontSize: 'clamp(0.9rem, 0.9vw, 1rem)',
                        }}
                      >
                        Monthly Revenue Breakdown by Customer
                      </Typography>

                      <TableContainer sx={{ maxHeight: { xs: 420, sm: 520, md: 600 }, overflow: 'auto' }}>
                        <Table
                          stickyHeader
                          sx={{
                            '& .MuiTableCell-root': {
                              fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.78rem' },
                              py: { xs: 0.65, sm: 0.9 },
                              px: { xs: 1, sm: 1.25 },
                            },
                            '& .MuiTableCell-head': {
                              fontSize: { xs: '0.76rem', sm: '0.8rem', md: '0.76rem' },
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, color: '#374151', minWidth: 200, position: 'sticky', left: 0, backgroundColor: '#f8fafc', zIndex: 1 }}>
                                Customer
                              </TableCell>
                              {customerTurnoverData.monthly_breakdown.map((monthData) => (
                                <TableCell key={monthData.month} align="center" sx={{ fontWeight: 600, color: '#374151', minWidth: 120 }}>
                                  {new Date(monthData.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                                </TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', minWidth: 120 }}>
                                Total
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#374151', minWidth: 120 }}>
                                % of Period
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {customerTurnoverData.period_summary
                              .filter(customer => customer.total_revenue > 0)
                              .map((customer) => (
                                <TableRow key={customer.customer_name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                      fontWeight: 600,
                                      position: 'sticky',
                                      left: 0,
                                      backgroundColor: '#ffffff',
                                      zIndex: 1,
                                      borderRight: '1px solid #e5e7eb'
                                    }}
                                  >
                                    {customer.customer_name}
                                  </TableCell>
                                  {customerTurnoverData.monthly_breakdown.map((monthData) => {
                                    const customerMonthData = monthData.customers.find(c => c.customer_name === customer.customer_name);
                                    const revenue = customerMonthData?.revenue || 0;
                                    const percentage = customerMonthData?.percentage || 0;

                                    return (
                                      <TableCell key={monthData.month} align="right" sx={{ borderRight: '1px solid #f1f5f9' }}>
                                        <Box>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 600,
                                              color: revenue > 0 ? '#374151' : '#9ca3af',
                                              fontSize: { xs: '0.78rem', sm: '0.82rem', md: '0.78rem' },
                                            }}
                                          >
                                            ${revenue.toLocaleString()}
                                          </Typography>
                                          {percentage > 0 && (
                                            <Chip
                                              label={`${percentage.toFixed(1)}%`}
                                              color={percentage > 20 ? 'primary' : percentage > 10 ? 'secondary' : 'default'}
                                              size="small"
                                              sx={{
                                                fontSize: { xs: '0.7rem', sm: '0.74rem', md: '0.7rem' },
                                                height: { xs: 18, sm: 20 },
                                                '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>
                                    ${customer.total_revenue.toLocaleString()}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>
                                    <Chip
                                      label={`${customer.percentage}%`}
                                      color={customer.percentage > 20 ? 'primary' : customer.percentage > 10 ? 'secondary' : 'default'}
                                      size="small"
                                      sx={{
                                        fontSize: { xs: '0.72rem', sm: '0.75rem', md: '0.72rem' },
                                        height: { xs: 18, sm: 20 },
                                        '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Box>
                ) : (
                  <Box sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 2, border: '2px dashed #d1d5db' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#6b7280',
                        mb: 1,
                        fontWeight: 700,
                        fontSize: 'clamp(0.9rem, 0.9vw, 1rem)',
                      }}
                    >
                      No Customer Turnover Data Available
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#9ca3af', fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' } }}
                    >
                      No data found for the selected period
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* EBITDA Period Analysis Section */}
          {financeData && !loading && financeData.monthly_data && financeData.monthly_data.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: '#0f172a',
                  fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                }}
              >
                EBITDA Period Analysis
              </Typography>

              <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: { xs: 1.5, sm: 2 },
                    color: '#0f172a',
                    fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                  }}
                >
                  Period Comparison Analysis
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    mb: { xs: 2, sm: 3 },
                    fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                  }}
                >
                  Compare two specific periods within the selected date range to analyze EBITDA changes
                </Typography>

                {/* Period Selection */}
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Period"
                        views={['year', 'month']}
                        value={startPeriod ? dayjs(startPeriod) : null}
                        onChange={(newValue) => setStartPeriod(newValue ? newValue.format('YYYY-MM') : '')}
                        minDate={startDate ? dayjs(startDate) : undefined}
                        maxDate={endDate ? dayjs(endDate) : undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            InputLabelProps: { shrink: true },
                            sx: {
                              backgroundColor: '#ffffff',
                              borderRadius: 2,
                              '& .MuiInputLabel-root': {
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.85rem' },
                              },
                              '& .MuiFormHelperText-root': {
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              },
                            },
                          }
                        }}
                        format="MMM YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Period"
                        views={['year', 'month']}
                        value={endPeriod ? dayjs(endPeriod) : null}
                        onChange={(newValue) => setEndPeriod(newValue ? newValue.format('YYYY-MM') : '')}
                        minDate={startDate ? dayjs(startDate) : undefined}
                        maxDate={endDate ? dayjs(endDate) : undefined}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            InputLabelProps: { shrink: true },
                            sx: {
                              backgroundColor: '#ffffff',
                              borderRadius: 2,
                              '& .MuiInputLabel-root': {
                                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
                              },
                              '& .MuiInputBase-input': {
                                fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.85rem' },
                              },
                              '& .MuiFormHelperText-root': {
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              },
                            },
                          }
                        }}
                        format="MMM YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                {/* Analysis Tables - Show when both periods are selected */}
                {startPeriod && endPeriod ? (
                  <Box>
                    {analysisLoading ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <CircularProgress />
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6b7280',
                            mt: 2,
                            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                          }}
                        >
                          Analyzing EBITDA changes...
                        </Typography>
                      </Box>
                    ) : analysisError ? (
                      <Alert
                        severity="error"
                        sx={{
                          mb: 2,
                          borderRadius: 2,
                          fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                        }}
                      >
                        {analysisError}
                      </Alert>
                    ) : ebitdaAnalysisData ? (
                      <Box>
                        {/* EBITDA Impact Analysis Table */}
                        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, backgroundColor: '#ffffff' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              mb: { xs: 1.5, sm: 2 },
                              color: '#374151',
                              fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                            }}
                          >
                            EBITDA Impact Analysis
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#6b7280',
                              mb: { xs: 2, sm: 3 },
                              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                            }}
                          >
                            Comparing: {getAvailableMonths().find(m => m.value === startPeriod)?.label} vs {getAvailableMonths().find(m => m.value === endPeriod)?.label}
                          </Typography>

                          <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(12px, 0.65vw, 13px)' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Component</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Start Period</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>End Period</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Change (₹)</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>% Change</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>EBITDA Impact</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>Revenue</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.start_period.revenue)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.end_period.revenue)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.revenue.absolute_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.revenue.absolute_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.revenue.percentage_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.ebitda_impact_analysis.changes.revenue.percentage_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.revenue.ebitda_impact >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.revenue.ebitda_impact)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>Cost of Sales</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.start_period.cost_of_sales)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.end_period.cost_of_sales)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.cost_of_sales.absolute_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.cost_of_sales.absolute_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.cost_of_sales.percentage_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.ebitda_impact_analysis.changes.cost_of_sales.percentage_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.cost_of_sales.ebitda_impact >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.cost_of_sales.ebitda_impact)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>SG&A Expenses</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.start_period.sga_expenses)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.end_period.sga_expenses)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.sga_expenses.absolute_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.sga_expenses.absolute_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.sga_expenses.percentage_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.ebitda_impact_analysis.changes.sga_expenses.percentage_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.sga_expenses.ebitda_impact >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.sga_expenses.ebitda_impact)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>Other Income</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.start_period.other_income)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.end_period.other_income)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.other_income.absolute_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.other_income.absolute_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.other_income.percentage_change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.ebitda_impact_analysis.changes.other_income.percentage_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.other_income.ebitda_impact >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.other_income.ebitda_impact)}
                                  </td>
                                </tr>
                                <tr style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>EBITDA</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.start_period.ebitda)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.end_period.ebitda)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.ebitda.absolute_change >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.ebitda.absolute_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.ebitda.percentage_change >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.ebitda_impact_analysis.changes.ebitda.percentage_change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.ebitda_impact_analysis.changes.ebitda.absolute_change >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.ebitda_impact_analysis.changes.ebitda.absolute_change)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </Box>
                        </Paper>

                        {/* Revenue Breakdown Analysis Table */}
                        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, backgroundColor: '#ffffff' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              mb: { xs: 1.5, sm: 2 },
                              color: '#374151',
                              fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                            }}
                          >
                            Revenue Breakdown Analysis
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#6b7280',
                              mb: { xs: 2, sm: 3 },
                              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                            }}
                          >
                            Detailed revenue analysis by geography and project
                          </Typography>

                          <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(12px, 0.65vw, 13px)' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Revenue Component</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Start Period</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>End Period</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Change (₹)</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>% Change</th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Total Revenue */}
                                <tr style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Total Revenue</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.total_revenue.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.total_revenue.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.total_revenue.change >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.total_revenue.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.total_revenue.change >= 0 ? '#059669' : '#dc2626',
                                    fontWeight: 600
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.total_revenue.percentage_change)}
                                  </td>
                                </tr>

                                {/* India Revenue */}
                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>India Revenue</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.total.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.total.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.total.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.total.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.total.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.total.percentage_change)}
                                  </td>
                                </tr>

                                {/* India Sub-projects */}
                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Autoliv</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.autoliv.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.autoliv.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.autoliv.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.autoliv.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.autoliv.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.autoliv.percentage_change)}
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Rina</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.rina.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.rina.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.rina.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.rina.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.rina.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.rina.percentage_change)}
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ MovieOne</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.movieone.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.movieone.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.movieone.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.movieone.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.movieone.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.movieone.percentage_change)}
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Nephrocare</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.nephrocare.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.nephrocare.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.nephrocare.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.nephrocare.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.nephrocare.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.nephrocare.percentage_change)}
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Open Assessment</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.open_assessment.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.open_assessment.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.open_assessment.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.open_assessment.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.open_assessment.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.open_assessment.percentage_change)}
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>└─ Others</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.others.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.others.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.others.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.others.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.others.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.india.sub_projects.others.percentage_change)}
                                  </td>
                                </tr>

                                {/* US Revenue */}
                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', fontWeight: 500 }}>US Revenue</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.total.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.total.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.us.total.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.total.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.us.total.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.us.total.percentage_change)}
                                  </td>
                                </tr>

                                {/* US Sub-projects */}
                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Slim</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.slim.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.slim.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.slim.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.slim.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.slim.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.slim.percentage_change)}
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 36px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>└─ Sequencer</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.sequencer.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.sequencer.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.sequencer.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.sequencer.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.sequencer.change >= 0 ? '#059669' : '#dc2626'
                                  }}>
                                    {formatPercentage(ebitdaAnalysisData.revenue_breakdown_analysis.us.sub_projects.sequencer.percentage_change)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </Box>
                        </Paper>

                        {/* Cost Efficiency Analysis Table */}
                        <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, backgroundColor: '#ffffff' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              mb: { xs: 1.5, sm: 2 },
                              color: '#374151',
                              fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                            }}
                          >
                            Cost Efficiency Analysis
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#6b7280',
                              mb: { xs: 2, sm: 3 },
                              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                            }}
                          >
                            Cost analysis showing efficiency relative to revenue
                          </Typography>

                          <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(12px, 0.65vw, 13px)' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Cost Component</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Start Period</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>End Period</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Change (₹)</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>% of Revenue</th>
                                  <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Efficiency Change</th>
                                </tr>
                              </thead>
                              <tbody>
                                {/* Cost of Sales */}
                                <tr style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Delivery Costs</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.change >= 0 ? '#dc2626' : '#059669',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.cost_pct_change >= 0 ? '#dc2626' : '#059669',
                                    fontWeight: 600
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.cost_pct_change >= 0 ? '#dc2626' : '#059669',
                                    fontWeight: 600
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.total.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                {/* Cost of Sales Sub-components */}
                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Software/Subscriptions</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.software_subscriptions.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Subcontracting</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.subcontracting.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Delivery Cost</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.delivery_cost.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>└─ Bench/Unallocated</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.cost_of_sales.sub_components.bench_unallocated.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                {/* SG&A Expenses */}
                                <tr style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>SG&A Expenses</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.change >= 0 ? '#dc2626' : '#059669',
                                    fontWeight: 600
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.cost_pct_change >= 0 ? '#dc2626' : '#059669',
                                    fontWeight: 600
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.cost_pct_change >= 0 ? '#dc2626' : '#059669',
                                    fontWeight: 600
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.total.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                {/* SG&A Sub-components */}
                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Corporate Staff</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.corporate_staff.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Director Remuneration</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.director_remuneration.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Rent</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.rent.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Travel</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.travel.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>├─ Business Promotion</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.business_promotion.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>

                                <tr>
                                  <td style={{ padding: '12px 12px 12px 24px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>└─ Professional Fees</td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.start_value)}
                                  </td>
                                  <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.end_value)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {formatCurrency(ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.change)}
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.start_cost_pct.toFixed(1)}% → {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.end_cost_pct.toFixed(1)}%
                                  </td>
                                  <td style={{
                                    padding: '12px',
                                    textAlign: 'right',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.cost_pct_change >= 0 ? '#dc2626' : '#059669'
                                  }}>
                                    {ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.cost_pct_change >= 0 ? '+' : ''}{ebitdaAnalysisData.cost_efficiency_analysis.sga_expenses.sub_components.professional_fees.cost_pct_change.toFixed(1)}%
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </Box>
                        </Paper>
                      </Box>
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 2, border: '2px dashed #d1d5db' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: '#6b7280',
                            mb: 1,
                            fontWeight: 700,
                            fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                          }}
                        >
                          EBITDA Analysis Tables
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#9ca3af',
                            mb: 2,
                            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                          }}
                        >
                          Comparing: {getAvailableMonths().find(m => m.value === startPeriod)?.label} vs {getAvailableMonths().find(m => m.value === endPeriod)?.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#9ca3af',
                            fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                          }}
                        >
                          Loading analysis data...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#ffffff', borderRadius: 2, border: '2px dashed #d1d5db' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#6b7280',
                        mb: 1,
                        fontWeight: 700,
                        fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                      }}
                    >
                      Select Periods to Compare
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#9ca3af',
                        fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.85rem' },
                      }}
                    >
                      Choose start and end periods from the dropdowns above to begin analysis
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* No Data State */}
          {!financeData && !loading && !error && startDate && endDate && (
            <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', backgroundColor: '#f8fafc' }}>
              <AccountBalance sx={{ fontSize: { xs: 48, sm: 56, md: 64 }, color: '#cbd5e1', mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: '#64748b',
                  fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                }}
              >
                No Financial Data Available
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#94a3b8', fontSize: { xs: '0.9rem', sm: '0.95rem', md: '0.9rem' } }}
              >
                No financial data found for the selected period
              </Typography>
            </Paper>
          )}

          {/* No Date Range State */}
          {!startDate || !endDate ? (
            <Paper sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', backgroundColor: '#f8fafc' }}>
              <AccountBalance sx={{ fontSize: { xs: 48, sm: 56, md: 64 }, color: '#cbd5e1', mb: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: '#64748b',
                  fontSize: 'clamp(0.95rem, 1.1vw, 1.05rem)',
                }}
              >
                Select Date Range
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#94a3b8', fontSize: { xs: '0.9rem', sm: '0.95rem', md: '0.9rem' } }}
              >
                Please select a date range from the header to view financial data
              </Typography>
            </Paper>
          ) : null}
        </>
    </Box>
  );
};

export default FinancePage;
